import { OPERATORS, type StationsFeature } from '@ev/shared'
import type {
  FilterSpecification,
  GeoJSONSourceSpecification,
  LayerSpecification,
} from 'maplibre-gl'
import {
  type MapTheme,
  OPERATOR_FALLBACK_COLOR,
  POWER_BAND_LIMITS,
  POWER_BAND_RADIUS,
  type PowerBand,
} from '../theme/palette'
import { CLUSTERING } from './config'

/**
 * The source and layer definitions for the station overlay.
 *
 * Kept as plain data so the component only has to add them and the palette only
 * has to supply colours. Rebuilding them is what a colour-scheme change costs:
 * MapLibre drops every custom layer when the basemap style is swapped, so these
 * are re-added rather than mutated.
 *
 * The band expressions below cut on `pmax` at exactly the thresholds in
 * `theme/palette.ts`, because a dot on the map and a chip in the detail panel
 * describing the same charger must not land in different bands.
 */

type CircleLayer = Extract<LayerSpecification, { type: 'circle' }>
type SymbolLayer = Extract<LayerSpecification, { type: 'symbol' }>
type CirclePaint = NonNullable<CircleLayer['paint']>
type CircleColor = NonNullable<CirclePaint['circle-color']>
type CircleRadius = NonNullable<CirclePaint['circle-radius']>

export const STATIONS_SOURCE_ID = 'stations'

/**
 * The visitor's own position, on its own source.
 *
 * Deliberately not a feature on the station source: that one clusters, and a
 * fix folded into a cluster would both hide the dot and inflate a charger count
 * by one.
 */
export const LOCATION_SOURCE_ID = 'user-location'

export const LAYER_IDS = {
  clusters: 'stations-clusters',
  clusterCount: 'stations-cluster-count',
  selectionHalo: 'stations-selection-halo',
  brand: 'stations-brand',
  core: 'stations-core',
  warning: 'stations-warning',
  selectionRing: 'stations-selection-ring',
} as const

export const LOCATION_LAYER_IDS = {
  halo: 'user-location-halo',
  dot: 'user-location-dot',
} as const

/** Layers a pointer can hit, topmost first. */
export const INTERACTIVE_LAYER_IDS = [LAYER_IDS.core, LAYER_IDS.brand, LAYER_IDS.clusters]

export interface StationsCollection {
  readonly type: 'FeatureCollection'
  readonly features: readonly StationsFeature[]
}

export const toCollection = (features: readonly StationsFeature[]): StationsCollection => ({
  type: 'FeatureCollection',
  features,
})

export const EMPTY_COLLECTION: StationsCollection = toCollection([])

/**
 * The site-level band, as a MapLibre expression.
 *
 * This is `siteBandFor` in `theme/palette.ts` written a second time, in the only
 * other language that has to agree with it. Everything the map paints from a
 * band - the dot colour, the dot radius, and the per-band cluster counters -
 * goes through this one helper, so a dot and the cluster it collapses into can
 * never disagree about which band a site is in.
 *
 * The rule: cut on `pmax`, except that a site with no DC point never reads above
 * `fast`, because 43 kW of three-phase AC is a 45-minute stop.
 */
const bandValue = <T>(slow: T, fast: T, rapid: T, ultra: T): unknown => [
  'case',
  ['==', ['get', 'dc'], 0],
  ['step', ['get', 'pmax'], slow, POWER_BAND_LIMITS.slow, fast],
  [
    'step',
    ['get', 'pmax'],
    slow,
    POWER_BAND_LIMITS.slow,
    fast,
    POWER_BAND_LIMITS.fast,
    rapid,
    POWER_BAND_LIMITS.rapid,
    ultra,
  ],
]

/** `1` when a site is in `band`, `0` otherwise - the cluster accumulators' input. */
const isBand = (band: PowerBand): unknown =>
  bandValue(
    ...((['slow', 'fast', 'rapid', 'ultra'] as const).map((id) => (id === band ? 1 : 0)) as [
      number,
      number,
      number,
      number,
    ]),
  )

/**
 * A cluster counts how many of its sites fall in each power band.
 *
 * The obvious aggregate - the strongest connector in the group - turns out to
 * say nothing at national zoom: across 9 000 sites almost every cluster
 * contains at least one 150 kW charger, so the whole country renders in the top
 * band's colour and the scale carries no information. Counting the bands
 * instead lets a cluster take the colour of the band *most* of its sites fall
 * in, which varies from region to region and is the thing worth seeing before
 * you decide where to zoom.
 */
export const stationsSourceSpec = (data: StationsCollection): GeoJSONSourceSpecification => ({
  type: 'geojson',
  data,
  cluster: true,
  clusterRadius: CLUSTERING.radius,
  clusterMaxZoom: CLUSTERING.maxZoom,
  clusterMinPoints: CLUSTERING.minPoints,
  clusterProperties: {
    slow: ['+', isBand('slow')],
    fast: ['+', isBand('fast')],
    rapid: ['+', isBand('rapid')],
    ultra: ['+', isBand('ultra')],
  } as GeoJSONSourceSpecification['clusterProperties'],
})

export interface LocationCollection {
  readonly type: 'FeatureCollection'
  readonly features: readonly {
    readonly type: 'Feature'
    readonly geometry: { readonly type: 'Point'; readonly coordinates: readonly [number, number] }
    readonly properties: Record<string, never>
  }[]
}

export const EMPTY_LOCATION: LocationCollection = { type: 'FeatureCollection', features: [] }

/**
 * The fix as a one-point collection, built by hand.
 *
 * Hand-built rather than parsed, and that is load-bearing: MapLibre serialises a
 * GeoJSON source by reading `value.constructor._classRegistryKey`, which is
 * `undefined` on the null-prototype objects funtypes returns, so a validated
 * value handed over here would be dropped silently. Nothing validates a
 * geolocation fix anyway - the browser hands over two numbers - so the only rule
 * is that these stay plain objects.
 */
export const toLocationCollection = (position: {
  readonly lat: number
  readonly lon: number
}): LocationCollection => ({
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [position.lon, position.lat] },
      properties: {},
    },
  ],
})

export const locationSourceSpec = (data: LocationCollection): GeoJSONSourceSpecification => ({
  type: 'geojson',
  data,
})

/**
 * The blue dot: a soft disc, then the dot itself with a white collar.
 *
 * Two layers rather than one circle with a wide stroke, because the halo has to
 * stay translucent while the dot stays opaque, and one circle cannot do both.
 * Neither is interactive - the dot is not a charger and must not swallow a click
 * meant for one underneath it.
 */
export const buildLocationLayers = (theme: MapTheme): LayerSpecification[] => {
  const halo: CircleLayer = {
    id: LOCATION_LAYER_IDS.halo,
    type: 'circle',
    source: LOCATION_SOURCE_ID,
    paint: {
      'circle-color': theme.locationHalo,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 10, 11, 16, 16, 26],
    },
  }

  const dot: CircleLayer = {
    id: LOCATION_LAYER_IDS.dot,
    type: 'circle',
    source: LOCATION_SOURCE_ID,
    paint: {
      'circle-color': theme.location,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 4.5, 11, 6, 16, 8],
      'circle-stroke-width': 2.5,
      'circle-stroke-color': theme.surface,
    },
  }

  return [halo, dot]
}

const IS_CLUSTER: FilterSpecification = ['has', 'point_count']
const IS_SITE: FilterSpecification = ['!', ['has', 'point_count']]

/** One site's colour, from the shared site-level band. */
const siteBandColor = (colors: Readonly<Record<PowerBand, string>>): CircleColor =>
  bandValue(colors.slow, colors.fast, colors.rapid, colors.ultra) as CircleColor

/**
 * A cluster's colour: the band holding most of its sites.
 *
 * Ties go to the faster band, which biases the map towards telling you that
 * fast charging is well represented rather than that it is not.
 */
const clusterBandColor = (colors: Readonly<Record<PowerBand, string>>): CircleColor =>
  [
    'case',
    [
      'all',
      ['>=', ['get', 'ultra'], ['get', 'rapid']],
      ['>=', ['get', 'ultra'], ['get', 'fast']],
      ['>=', ['get', 'ultra'], ['get', 'slow']],
    ],
    colors.ultra,
    ['all', ['>=', ['get', 'rapid'], ['get', 'fast']], ['>=', ['get', 'rapid'], ['get', 'slow']]],
    colors.rapid,
    ['>=', ['get', 'fast'], ['get', 'slow']],
    colors.fast,
    colors.slow,
  ] as CircleColor

/** Band radius at a given scale factor, so the ordering survives colour blindness. */
const bandRadius = (scale: number, extra = 0): CircleRadius =>
  bandValue(
    POWER_BAND_RADIUS.slow * scale + extra,
    POWER_BAND_RADIUS.fast * scale + extra,
    POWER_BAND_RADIUS.rapid * scale + extra,
    POWER_BAND_RADIUS.ultra * scale + extra,
  ) as CircleRadius

/**
 * Dots grow with zoom: small enough at national zoom that a dense city is a
 * texture rather than a blob, large enough at street zoom to be a target.
 */
const zoomedRadius = (extra = 0): CircleRadius =>
  [
    'interpolate',
    ['linear'],
    ['zoom'],
    5,
    bandRadius(0.62, extra * 0.62),
    11,
    bandRadius(1, extra),
    16,
    bandRadius(1.5, extra * 1.5),
  ] as CircleRadius

/** Operator slug -> registry colour. Anything unrecognised takes the `other` grey. */
const operatorColorExpression = (): CircleColor => {
  const stops: string[] = []
  OPERATORS.forEach((operator) => {
    stops.push(operator.slug, operator.color)
  })
  return ['match', ['get', 'op'], ...stops, OPERATOR_FALLBACK_COLOR] as unknown as CircleColor
}

/** Matches nothing: the selection layers' filter while no site is selected. */
export const NO_SELECTION_FILTER: FilterSpecification = ['==', ['get', 'id'], '']

export const selectionFilter = (siteId: string | null): FilterSpecification =>
  siteId === null ? NO_SELECTION_FILTER : ['==', ['get', 'id'], siteId]

export const buildLayers = (
  theme: MapTheme,
  selectedSiteId: string | null,
): LayerSpecification[] => {
  const clusters: CircleLayer = {
    id: LAYER_IDS.clusters,
    type: 'circle',
    source: STATIONS_SOURCE_ID,
    filter: IS_CLUSTER,
    paint: {
      'circle-color': clusterBandColor(theme.clusterBand),
      'circle-opacity': 0.94,
      'circle-radius': ['step', ['get', 'point_count'], 13, 10, 17, 50, 21, 200, 26, 1000, 32],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': theme.surface,
      'circle-stroke-opacity': 0.85,
    },
  }

  const clusterCount: SymbolLayer = {
    id: LAYER_IDS.clusterCount,
    type: 'symbol',
    source: STATIONS_SOURCE_ID,
    filter: IS_CLUSTER,
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['Noto Sans Bold'],
      'text-size': ['step', ['get', 'point_count'], 11, 50, 12, 200, 13],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': theme.clusterText,
      'text-halo-color': theme.clusterTextHalo,
      'text-halo-width': 0.6,
    },
  }

  const selectionHalo: CircleLayer = {
    id: LAYER_IDS.selectionHalo,
    type: 'circle',
    source: STATIONS_SOURCE_ID,
    filter: selectionFilter(selectedSiteId),
    paint: {
      'circle-color': theme.selectionHalo,
      'circle-radius': zoomedRadius(12),
    },
  }

  const brand: CircleLayer = {
    id: LAYER_IDS.brand,
    type: 'circle',
    source: STATIONS_SOURCE_ID,
    filter: IS_SITE,
    paint: {
      'circle-color': operatorColorExpression(),
      'circle-radius': zoomedRadius(3),
    },
  }

  const core: CircleLayer = {
    id: LAYER_IDS.core,
    type: 'circle',
    source: STATIONS_SOURCE_ID,
    filter: IS_SITE,
    paint: {
      'circle-color': siteBandColor(theme.band),
      'circle-radius': zoomedRadius(),
      'circle-stroke-width': 1.4,
      'circle-stroke-color': theme.surface,
    },
  }

  /**
   * The unconfirmed marker, sitting on the shoulder of the dot it belongs to.
   *
   * A symbol layer rather than a second circle, because it has to read as a
   * warning at a glance and a coloured ring beside a coloured dot would read as
   * another band. `text-allow-overlap` and `text-ignore-placement` are both on:
   * MapLibre would otherwise drop the mark wherever pins crowd, and a warning
   * that disappears when the map gets busy is worse than no warning - it would
   * quietly promote a doubtful site to a settled one at exactly the zoom where
   * the reader is comparing several.
   *
   * One mark for every reason. `unc` names which, and the hover card reads it,
   * but the pin is 12 px of glyph and cannot say more than "look at this one" -
   * a second symbol for the second reason would be two marks nobody can tell
   * apart at a glance. Hence `has` rather than a comparison: the layer draws
   * wherever the key is present, and a third reason needs nothing here.
   */
  const warning: SymbolLayer = {
    id: LAYER_IDS.warning,
    type: 'symbol',
    source: STATIONS_SOURCE_ID,
    filter: ['all', IS_SITE, ['has', 'unc']],
    layout: {
      'text-field': '!',
      'text-font': ['Noto Sans Bold'],
      'text-size': 12,
      'text-offset': [0.85, -0.85],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': theme.warning,
      'text-halo-color': theme.surface,
      'text-halo-width': 1.6,
    },
  }

  const selectionRing: CircleLayer = {
    id: LAYER_IDS.selectionRing,
    type: 'circle',
    source: STATIONS_SOURCE_ID,
    filter: selectionFilter(selectedSiteId),
    paint: {
      'circle-color': theme.selection,
      'circle-opacity': 0,
      'circle-radius': zoomedRadius(4.5),
      'circle-stroke-width': 2.5,
      'circle-stroke-color': theme.selection,
    },
  }

  // Bottom to top: clusters, their labels, then the selection halo under the
  // dots, the dots themselves, the unconfirmed marks over them, and finally the
  // selection ring over everything.
  return [clusters, clusterCount, selectionHalo, brand, core, warning, selectionRing]
}
